import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { ButtonAppearance } from '../../Atoms/Button';

import { linkFormater } from './linkFormater';

import { Color } from '../../../shared/constants/Colors';

import * as S from './LanguageSelect.styles';

const LANGUAGE = {
  EN: 'English',
  FR: 'French',
  ES: 'Spanish',
  DE: 'German',
  IT: 'Italian',
};

const POSITION = {
  1: '-70px',
  2: '-110px',
  3: '-150px',
  4: '-185px',
};

export const LanguageSelect = ({ alternateLanguages, lang, ariaLabel }) => {
  if (!lang) {
    return null;
  }

  const [isOpen, setIsOpen] = useState(false);

  const langCode = lang.slice(0, 2).toUpperCase();
  const displayedLang = LANGUAGE[langCode];
  const altLangs = alternateLanguages?.filter(l => l.lang !== lang) || [];
  const hasNoAltLang = !!(!alternateLanguages || alternateLanguages.length < 1);

  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <S.DropdownContainer ref={dropdownRef}>
      <S.ButtonWrapper isDisabled={hasNoAltLang}>
        <S.GlobeIcon isOpen={isOpen} isDisabled={hasNoAltLang} />
        <S.StyledButton
          isOpen={isOpen}
          isIconOnRight={true}
          iconColor={Color.CLOUD_BURST}
          appearance={ButtonAppearance.MINIMAL}
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsOpen(prev => !prev);
            }
          }}
          isDisabled={hasNoAltLang}
          aria-haspopup='listbox'
          aria-controls='language-menu'
          aria-expanded={isOpen}
          aria-label={ariaLabel}
        >
          {!hasNoAltLang && <S.AngleIcon isOpen={isOpen} />}
          {displayedLang}
        </S.StyledButton>
      </S.ButtonWrapper>

      {isOpen && (
        <S.DropdownMenu
          topPosition={POSITION[altLangs.length]}
          role='listbox'
          id='language-menu'
        >
          {altLangs.map((language, index) => {
            const displayedLanguage =
              LANGUAGE[language.lang.slice(0, 2).toUpperCase()];

            return (
              <S.Option
                key={`${language.lang}-${index}`}
                role='option'
                aria-selected={language.lang.slice(0, 2)}
                href={linkFormater(language)}
                tabIndex='0'
              >
                {displayedLanguage}
              </S.Option>
            );
          })}
        </S.DropdownMenu>
      )}
    </S.DropdownContainer>
  );
};

LanguageSelect.propTypes = {
  /**
   * Array of language data including url, type, lang and uid.
   */
  alternateLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      lang: PropTypes.string,
      type: PropTypes.string,
      uid: PropTypes.string,
    })
  ),
  /**
   * Current language (ISO language code).
   */
  lang: PropTypes.string.isRequired,
  /**
   * Aria label for the button.
   */
  ariaLabel: PropTypes.string,
};
